import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
// Assuming TalkytoonLogo is a fun, high-quality image
import TalkytoonLogo from "../Photos/Logotalkytoon.png";
import tokogif from "../Photos/TokoHeader.png"
import {
  ChevronDown,
  Gamepad,
  BookOpen,
  PenTool,
  Music,
  Gift,
  Trophy,
  Video,
  Type,
  Calculator,
  PawPrint,
  FlaskRound,
  HeartHandshake,
  Sparkles,
  LayoutDashboard,
  LogOut,
  Home,
  UserCircle,
  X,
  Menu, // Using a more generic user icon for non-avatar login state
} from "lucide-react";

// ✅ Firebase
import {
  getAuth,
  onAuthStateChanged,
  signOut
} from "firebase/auth";
import {
  getFirestore,
  doc,
  getDoc
} from "firebase/firestore";

const LANGUAGE_OPTIONS = [
  { code: "EN", name: "English" },
  { code: "ES", name: "Spanish" },
  { code: "FR", name: "French" },
  { code: "DE", name: "German" },
  { code: "NL", name: "Dutch" },
  { code: "HI", name: "Hindi" },
  { code: "ZH", name: "Mandarin" },
  { code: "PT", name: "Portuguese" },
  { code: "JA", name: "Japanese" },
  { code: "AR", name: "Arabic" },
];

// ✅ Navbar Dropdown Structure (Logic Unchanged))))
const navStructure = [
  {
    name: "Learn",
    type: "dropdown",
    links: [
      { name: "Alphabet A–Z", path: "/alphabet", icon: <Type className="w-5 h-5 mr-3" /> },
      { name: "Numbers & Math", path: "/numbers", icon: <Calculator className="w-5 h-5 mr-3" /> },
      { name: "Reading & Phonics", path: "/phonics", icon: <BookOpen className="w-5 h-5 mr-3" /> },
      { name: "Science Basics", path: "/science", icon: <FlaskRound className="w-5 h-5 mr-3" /> },
      { name: "Life Skills", path: "/lifeskills", icon: <Sparkles className="w-5 h-5 mr-3" /> },
    ],
  },
  {
    name: "Play",
    type: "dropdown",
    links: [
      { name: "Games", path: "/playgames", icon: <Gamepad className="w-5 h-5 mr-3" /> },
      { name: "Puzzle Corner", path: "/puzzlecorner", icon: <Gamepad className="w-5 h-5 mr-3" /> },
      { name: "Create & Draw", path: "/createdraw", icon: <PenTool className="w-5 h-5 mr-3" /> },
      { name: "Animals & Sounds", path: "/animals", icon: <PawPrint className="w-5 h-5 mr-3" /> },
      { name: "Music & Dance", path: "/musicdance", icon: <Music className="w-5 h-5 mr-3" /> },
    ],
  },

  {
    name: "Stories",
    type: "dropdown",
    links: [
      { name: "Story Time", path: "/storytime", icon: <BookOpen className="w-5 h-5 mr-3" /> },
      { name: "Moral Stories", path: "/moralstories", icon: <HeartHandshake className="w-5 h-5 mr-3" /> },
    ],
  },

  {
    name: "Parents",
    type: "dropdown",
    links: [
      { name: "Parent Dashboard", path: "/parentcontrol", icon: <LayoutDashboard className="w-5 h-5 mr-3" /> },
      // { name: "Learning Tips", path: "/learningtips", icon: <BookOpen className="w-5 h-5 mr-3" /> },
      // { name: "Skill Development", path: "/skilldevelopment", icon: <PenTool className="w-5 h-5 mr-3" /> },
    ],
  },
  // {
  //   name: "Shop",
  //   type: "link",
  //   path: "/shop",
  //   icon: <Gift className="w-5 h-5 mr-2" />,
  // },

  {
    name: "Events",
    type: "dropdown",
    links: [
      { name: "Workshops", path: "/workshops", icon: <PenTool className="w-5 h-5 mr-3" /> },
      // { name: "Competitions", path: "/competitions", icon: <Trophy className="w-5 h-5 mr-3" /> },
      // { name: "Webinars", path: "/webinars", icon: <Video className="w-5 h-5 mr-3" /> },
    ],
  },
];
  

// ✅ Desktop Dropdown Component
const DesktopDropdown = ({ item, openDropdown, setOpenDropdown }) => {
  const isOpen = openDropdown === item.name;

  const toggleDropdown = (e) => {
    e.stopPropagation();
    setOpenDropdown(isOpen ? null : item.name);
  }

  return (
    <div className="relative flex items-center h-full">
      <button
        onClick={toggleDropdown}
        // Medium font size: text-lg for main navigation
        className={`flex items-center space-x-1 px-5 py-2 text-lg rounded-2xl font-bold transition h-full ${isOpen
          ? "bg-blue-600 text-white shadow-lg transform scale-105"
          : "text-gray-800 hover:bg-blue-100 hover:text-blue-600"
          }`}
      >
        <span>{item.name}</span>
        <ChevronDown
          className={`w-5 h-5 transition-transform ${isOpen ? "rotate-180 text-white" : "rotate-0 text-blue-400"}`}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 min-w-[250px] bg-white border-2 border-yellow-400 rounded-2xl shadow-2xl z-50 p-2 overflow-hidden">
          {item.links.map((link) => (
            <NavLink
              key={link.name}
              to={link.path}
              onClick={() => setOpenDropdown(null)}
              // Medium font size: text-base for dropdown links
              className={({ isActive }) =>
                `flex items-center px-4 py-3 text-base font-semibold transition duration-150 m-1 rounded-xl 
                                ${isActive
                  ? "bg-yellow-400 text-blue-800 shadow-inner"
                  : "text-gray-700 hover:bg-yellow-100 hover:text-blue-600"
                }`
              }
            >
              {link.icon} {link.name}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
};

// ✅ Language Dropdown (Kept small as it's a utility)
const LanguageDropdown = ({ language, setLanguage }) => {
  const [open, setOpen] = useState(false);
  const currentLang =
    LANGUAGE_OPTIONS.find((l) => l.code === language)?.name ||
    "Language";

  useEffect(() => {
    if (!open) return;
    const closeLang = (e) => {
      if (!e.target.closest('.language-dropdown-container')) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', closeLang);
    return () => document.removeEventListener('mousedown', closeLang);
  }, [open]);

  return (
    <div className="relative language-dropdown-container">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center px-4 py-2 rounded-full bg-blue-100 hover:bg-blue-200 text-sm font-medium text-blue-700 transition"
      >
        {currentLang}
        <ChevronDown
          className={`w-4 h-4 ml-1 transition-transform ${open ? "rotate-180" : ""
            }`}
        />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-36 bg-white border border-blue-200 rounded-xl shadow-lg z-50 p-1">
          {LANGUAGE_OPTIONS.map((lang) => (
            <button
              key={lang.code}
              onClick={() => {
                setLanguage(lang.code);
                setOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-sm text-gray-700 rounded-lg hover:bg-yellow-100 hover:text-yellow-700 transition ${lang.code === language ? 'bg-yellow-50 font-semibold' : ''}`}
            >
              {lang.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ✅ User Menu Dropdown (Kept small as it's a utility)
const UserMenuDropdown = ({ currentUser, handleLogout, userMenuOpen, setUserMenuOpen }) => {
  return (
    <div className="relative user-menu-dropdown-container">
      <button
        onClick={() => setUserMenuOpen(!userMenuOpen)}
        className="flex items-center space-x-2 p-1.5 rounded-full bg-emerald-500 hover:bg-emerald-600 transition shadow-lg"
      >
        <img
          src={currentUser.avatar || 'https://i.pravatar.cc/40?img=1'}
          className="w-8 h-8 rounded-full border-2 border-white"
          alt="User Avatar"
        />
        <span className="font-bold text-sm text-white hidden lg:inline mr-1">
          {currentUser.name || currentUser.email?.split('@')[0] || "Hi, Kiddo!"}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-white transition-transform ${userMenuOpen ? "rotate-180" : ""
            }`}
        />
      </button>

      {userMenuOpen && (
        <div className="absolute right-0 mt-2 w-52 bg-white border-2 border-emerald-300 rounded-xl shadow-2xl z-50 p-1 overflow-hidden">
          <NavLink
            to="/parentcontrol"
            onClick={() => setUserMenuOpen(false)}
            className="flex items-center px-4 py-2 text-base text-gray-700 rounded-lg hover:bg-blue-100 hover:text-blue-700 transition"
          >
            <LayoutDashboard className="w-5 h-5 mr-2 text-blue-500" /> Parent Dashboard
          </NavLink>

          <button
            onClick={handleLogout}
            className="w-full flex items-center text-left px-4 py-2 text-base text-red-600 rounded-lg hover:bg-red-100 transition border-t mt-1 pt-2 border-gray-100"
          >
            <LogOut className="w-5 h-5 mr-2 text-red-500" /> Logout
          </button>
        </div>
      )}
    </div>
  );
};


// ✅ Main Navbar Component
const Navbar = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [language, setLanguage] = useState("EN");
  const [openDropdown, setOpenDropdown] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Close dropdowns logic
  useEffect(() => {
    const closeDropdown = (e) => {
      if (openDropdown || userMenuOpen) {
        if (!e.target.closest('.relative.flex') && !e.target.closest('.user-menu-dropdown-container') && !e.target.closest('.language-dropdown-container')) {
          setOpenDropdown(null);
          setUserMenuOpen(false);
        }
      }
    };
    document.addEventListener('mousedown', closeDropdown);
    return () => document.removeEventListener('mousedown', closeDropdown);
  }, [openDropdown, userMenuOpen]);

  // Auth Listener
  useEffect(() => {
    const auth = getAuth();
    const db = getFirestore();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        setCurrentUser(userSnap.exists() ? { uid: user.uid, ...userSnap.data(), email: user.email } : { email: user.email });
      } else { setCurrentUser(null); }
    });
    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    await signOut(getAuth());
    setUserMenuOpen(false);
    setMobileMenuOpen(false);
    navigate("/login");
  };

  const dropdowns = navStructure.filter(i => i.type === 'dropdown');

  return (
    <nav className="sticky top-0 z-50 shadow-2xl font-sans">
      
      {/* ✅ TOP BAR: Desktop Utility (Shop + Auth + Language) */}
      <div className="hidden md:block border-b-2 border-yellow-400 bg-gradient-to-r from-blue-700 to-cyan-500 text-white shadow-lg">
        <div className="max-w-7xl mx-auto flex justify-between items-center px-4 py-2">
          
          {/* Left: Mascot & Slogan */}
          <div className="flex items-center space-x-3 text-base font-bold ml-[-0.5rem]">
             <img src={tokogif} alt="Toko" className="w-10 h-10 animate-bounce" />
             <p className="flex items-center gap-2">Play. Learn. Imagine. Repeat! <span className="text-yellow-300 animate-pulse">💡</span></p>
          </div>

          {/* Right Section: Shop, Auth, Language */}
          <div className="flex items-center space-x-4">
            
            {/* Desktop Shop Button */}
            {/* <NavLink to="/shop" className="flex items-center gap-2 px-4 py-1.5 rounded-full font-extrabold text-white text-sm bg-pink-600 hover:bg-pink-700 transition-all shadow-md transform hover:scale-105 active:scale-95">
              <Gift size={18} /> Shop
            </NavLink> */}

            <div className="h-6 w-[2px] bg-white/20 mx-1" /> {/* Divider */}

            {/* Desktop Language Selector */}
            {/* <LanguageDropdown language={language} setLanguage={setLanguage} /> */}

            {/* Desktop Auth Section */}
            {currentUser ? (
              <UserMenuDropdown 
                currentUser={currentUser} 
                handleLogout={handleLogout} 
                userMenuOpen={userMenuOpen} 
                setUserMenuOpen={setUserMenuOpen} 
              />
            ) : (
              <div className="flex items-center space-x-3">
                <NavLink to="/login" className="text-sm font-bold border-2 border-white/50 px-4 py-1.5 rounded-full hover:bg-white hover:text-blue-700 transition">
                  Login
                </NavLink>
                <NavLink to="/register" className="text-sm font-bold bg-emerald-400 text-blue-900 px-4 py-2 rounded-full hover:bg-emerald-500 transition shadow-md transform hover:scale-105">
                  Sign Up
                </NavLink>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ✅ MAIN WHITE BAR (Logo + Main Nav) */}
      <div className="bg-white border-b-4 border-yellow-400 shadow-xl relative z-40">
        <div className="max-w-7xl mx-auto flex justify-between items-center px-4 h-16 md:h-20">
          
          <NavLink to="/" className="flex items-center shrink-0 transform hover:scale-[1.02] transition duration-200">
            <img src={TalkytoonLogo} alt="Logo" className="h-14 md:h-24 w-auto object-contain my-[-0.5rem]" />
          </NavLink>

          {/* Mobile Right Controls */}
          <div className="flex items-center space-x-2 md:hidden">
            {/* <LanguageDropdown language={language} setLanguage={setLanguage} /> */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`p-2 rounded-xl transition-all ${mobileMenuOpen ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'}`}
            >
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center space-x-2 lg:space-x-4">
            <NavLink to="/" className={({ isActive }) => `px-5 py-2 rounded-2xl text-lg font-bold transition ${isActive ? "bg-yellow-400 text-blue-800 shadow-inner" : "text-gray-800 hover:bg-yellow-100"}`}>
              Home
            </NavLink>
            {dropdowns.map((item) => (
              <DesktopDropdown key={item.name} item={item} openDropdown={openDropdown} setOpenDropdown={setOpenDropdown} />
            ))}
          </div>
        </div>
      </div>

      {/* ✅ MOBILE OVERLAY MENU (Optimized Layout) */}
      <div className={`fixed inset-0 top-[64px] bg-blue-50/95 backdrop-blur-md transition-all duration-500 md:hidden overflow-y-auto ${mobileMenuOpen ? "opacity-100 translate-x-0" : "opacity-0 translate-x-full pointer-events-none"}`}>
        <div className="p-4 space-y-6 pb-24">
          
          {/* Mobile Auth Header */}
          {!currentUser && (
            <div className="grid grid-cols-2 gap-3">
              <NavLink to="/login" onClick={() => setMobileMenuOpen(false)} className="text-center bg-blue-600 text-white font-bold py-4 rounded-2xl shadow-lg">Login</NavLink>
              <NavLink to="/register" onClick={() => setMobileMenuOpen(false)} className="text-center bg-emerald-500 text-white font-bold py-4 rounded-2xl shadow-lg">Sign Up</NavLink>
            </div>
          )}

          {/* Quick Nav Cards */}
          <div className="grid grid-cols-2 gap-3">
            <NavLink to="/" onClick={() => setMobileMenuOpen(false)} className="flex flex-col items-center justify-center p-4 bg-white border-2 border-blue-100 rounded-2xl">
              <div className="p-3 bg-blue-50 rounded-full text-blue-600 mb-2"><Home size={24} /></div>
              <span className="font-bold text-gray-800">Home</span>
            </NavLink>
            {/* <NavLink to="/shop" onClick={() => setMobileMenuOpen(false)} className="flex flex-col items-center justify-center p-4 bg-white border-2 border-pink-100 rounded-2xl">
              <div className="p-3 bg-pink-50 rounded-full text-pink-600 mb-2"><Gift size={24} /></div>
              <span className="font-bold text-gray-800">Shop</span>
            </NavLink> */}
          </div>

          {/* Navigation Groups */}
          {dropdowns.map((group) => (
            <div key={group.name} className="space-y-2">
              <h3 className="text-xs font-black uppercase tracking-widest text-blue-400 px-2">{group.name}</h3>
              <div className="bg-white rounded-3xl border-2 border-gray-100 shadow-sm">
                {group.links.map((link) => (
                  <NavLink
                    key={link.name}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center px-5 py-4 border-b border-gray-50 last:border-0"
                  >
                    <span className="mr-4 text-blue-500">{React.cloneElement(link.icon, { className: "w-6 h-6" })}</span>
                    <span className="font-bold text-gray-700">{link.name}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          ))}

          {/* Logout for Mobile */}
          {currentUser && (
             <button onClick={handleLogout} className="w-full p-4 bg-red-50 text-red-600 rounded-2xl font-bold border-2 border-red-100">
               Logout
             </button>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;